import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, validatePasswordReset } from "../../api/api";
import CreatePasswordField from "../../widgets/CreatePasswordField";
import { Button } from "../ui/button";

/**
 * Set a new password from an emailed reset token, then return to login.
 * @returns {JSX.Element}
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setTokenError("This reset link is invalid or expired.");
        setChecking(false);
        return;
      }
      const result = await validatePasswordReset(token);
      if (cancelled) return;
      if (result.error || result.success === false) {
        setTokenError(result.error || "This reset link is invalid or expired.");
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * @param {React.FormEvent} event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!passwordValid) return;
    setSubmitting(true);
    const result = await confirmPasswordReset(token, password);
    setSubmitting(false);
    if (result.error || result.success === false) {
      setSubmitError(result.error || "Failed to reset password");
      return;
    }
    navigate("/login", { replace: true, state: { passwordReset: true } });
  };

  return (
    <main className="relative z-10 overflow-x-hidden text-text-primary">
      <section className="relative flex min-h-[calc(100dvh-3.5rem)] w-full flex-col items-center justify-center gap-8 overflow-x-hidden px-4 py-12 text-center md:gap-10 md:py-16">
        <div className="relative mx-auto w-full max-w-4xl">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-hero-glass-border bg-hero-glass-bg px-6 py-8 shadow-staff-card backdrop-blur-sm md:max-w-lg md:px-10 md:py-10">
            <h1 className="mb-8 text-3xl font-bold text-hero-heading md:text-4xl">
              Reset password
            </h1>

            {checking ? (
              <p className="text-sm text-hero-subheading">Checking reset link…</p>
            ) : null}

            {!checking && tokenError ? (
              <>
                <p
                  role="alert"
                  className="mb-6 rounded-md border border-error bg-[var(--color-error-bg)] px-3 py-2 text-left text-sm text-error backdrop-blur-3xl"
                >
                  {tokenError}
                </p>
                <div className="flex flex-col gap-3">
                  <Button asChild className="h-12 w-full rounded-full">
                    <Link to="/forgot-password">Request a new link</Link>
                  </Button>
                  <Button asChild variant="outline" className="h-12 w-full rounded-full">
                    <Link to="/login">Back to login</Link>
                  </Button>
                </div>
              </>
            ) : null}

            {!checking && !tokenError ? (
              <form onSubmit={handleSubmit} className="space-y-5 text-left">
                <CreatePasswordField
                  id="reset-password"
                  value={password}
                  onChange={setPassword}
                  showConfirm
                  confirmValue={confirmPassword}
                  onConfirmChange={setConfirmPassword}
                  onValidityChange={setPasswordValid}
                  inputClassName="h-11 border-hero-glass-border bg-hero-glass-bg text-hero-heading shadow-none backdrop-blur-sm"
                />
                {submitError ? (
                  <p className="text-sm text-error" role="alert">
                    {submitError}
                  </p>
                ) : null}
                <Button
                  type="submit"
                  disabled={submitting || !passwordValid}
                  className="h-12 w-full rounded-full bg-hero-cta-primary px-8 font-semibold text-hero-cta-primary-fg shadow-lg transition-[transform,box-shadow] duration-200 ease-out hover:bg-hero-cta-primary-hover motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-xl active:scale-[0.98]"
                >
                  {submitting ? "Saving…" : "Update password"}
                </Button>
              </form>
            ) : null}

            {!checking && !tokenError ? (
              <p className="mt-6 text-sm text-hero-subheading">
                <Link
                  to="/login"
                  className="font-semibold text-hero-heading underline decoration-hero-heading decoration-2 underline-offset-[5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                >
                  Back to login
                </Link>
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}
